"use client";

import type { ReactNode } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * A labelled on/off row, styled as a switch.
 *
 * A real `<input type="checkbox" role="switch">` rather than a div with an
 * onClick, so Tab, Space and screen readers work without extra code. Note this
 * is NOT components/form/switch.tsx, which is a click-only div and cannot be
 * reached from the keyboard — do not swap one for the other.
 */
export function ToggleSwitch({
  name,
  description,
  checked,
  disabled = false,
  icon,
  onToggle,
  onMove,
  canMoveUp = false,
  canMoveDown = false,
}: {
  name: string;
  description: ReactNode;
  checked: boolean;
  /** A row that cannot be switched off still shows its state, greyed. */
  disabled?: boolean;
  /**
   * Decorative mark before the label — the drawer shows each navigation area's
   * own icon. Omitted everywhere else; the name carries the meaning, so it is
   * hidden from assistive technology rather than labelled twice.
   */
  icon?: ReactNode;
  onToggle: () => void;
  /** Omit to render no arrows at all. */
  onMove?: (direction: -1 | 1) => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
}) {
  return (
    // The arrows sit OUTSIDE the <label>. React's stopPropagation only stops
    // the synthetic event — the native click still reaches the label, and a
    // label activates its control however the click got there. Nesting them
    // meant every arrow press also flipped the switch.
    <div
      className={`flex items-center gap-3 rounded-lg border border-border p-3 transition-colors ${
        disabled ? "" : "hover:bg-accent/40"
      }`}
    >
      {onMove && (
        // Buttons, not a drag handle: this list is four to eight rows long and
        // arrows are reachable from a keyboard without a drag-and-drop
        // fallback.
        <span className="flex shrink-0 flex-col">
          <button
            type="button"
            aria-label={`Move ${name} up`}
            disabled={!canMoveUp}
            onClick={() => onMove(-1)}
            className="rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30 disabled:hover:text-muted-foreground"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label={`Move ${name} down`}
            disabled={!canMoveDown}
            onClick={() => onMove(1)}
            className="rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30 disabled:hover:text-muted-foreground"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </span>
      )}

      <label
        className={`flex min-w-0 flex-1 items-center gap-3 ${
          disabled ? "" : "cursor-pointer"
        }`}
      >
        {icon && (
          <span aria-hidden className="shrink-0 text-muted-foreground">
            {icon}
          </span>
        )}

        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-sm font-medium text-foreground">{name}</span>
          <span className="text-xs text-muted-foreground">{description}</span>
        </span>

        <span className="relative inline-flex shrink-0">
          <input
            type="checkbox"
            role="switch"
            className="peer sr-only"
            checked={checked}
            disabled={disabled}
            onChange={() => !disabled && onToggle()}
          />
          <span
            aria-hidden
            // The OFF track is muted-foreground at 30%, not bg-muted. In the
            // dark presets --muted is L=16% and --background (the knob) is
            // L=7%: a dark knob on a barely-lighter track, near invisible. A
            // translucent mid grey separates from the knob in both modes.
            className="h-6 w-11 rounded-full bg-muted-foreground/30 transition-colors peer-checked:bg-primary peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-background peer-disabled:opacity-50"
          />
          <span
            aria-hidden
            className="pointer-events-none absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-background shadow transition-transform peer-checked:translate-x-5 peer-disabled:opacity-50"
          />
        </span>
      </label>
    </div>
  );
}
